/**
 * WhereBox Public JavaScript
 *
 * Handles frontend interactions for the WhereBox plugin including
 * search functionality, item filtering, and interactive map features.
 *
 * @package WhereBox
 * @since 1.0.0
 */

// Import styles
import '../scss/main.scss';

(function($) {
    'use strict';

    // Check if jQuery is available
    if (typeof $ === 'undefined') {
        console.error('WhereBox: jQuery is not loaded');
        return;
    }

    /**
     * WhereBox Public Object
     *
     * Main object containing all public-facing functionality.
     *
     * @since 1.0.0
     */
    const WhereBoxPublic = {

        /**
         * Initialize the public scripts
         *
         * Sets up event listeners and initializes components.
         *
         * @since 1.0.0
         */
        init() {
            console.log('WhereBox Public: Initializing...');
            this.bindEvents();
            this.initSearch();
            this.initMap();
            this.initAccessibility();
            this.initCollapsibleSections();
            this.triggerAnimations();
            console.log('WhereBox Public: Initialized successfully');
        },

        /**
         * Bind event listeners
         *
         * Attaches event handlers to interactive elements.
         *
         * @since 1.0.0
         */
        bindEvents() {
            // Item card interactions
            $(document).on('click', '.item-card', this.handleItemClick);
            $(document).on('keypress', '.item-card', this.handleItemKeypress);

            // Collapsible section triggers
            $(document).on('click', '.collapsible-trigger', this.handleCollapsibleClick);
            $(document).on('keypress', '.collapsible-trigger', this.handleCollapsibleKeypress);

            // Room card interactions (non-collapsible)
            $(document).on('click', '.room-card:not(.collapsible-trigger)', this.handleRoomClick);

            // Search form enhancements
            $(document).on('submit', '.search-form', this.handleSearchSubmit);
            $(document).on('input', '.search-form input[type="text"]', this.debounce(this.handleSearchInput, 300));

            // Filter interactions
            $(document).on('change', '.wherebox-filters select', this.handleFilterChange);
        },

        /**
         * Initialize search functionality
         *
         * Sets up enhanced search features and auto-complete.
         *
         * @since 1.0.0
         */
        initSearch() {
            const $searchInput = $('.search-form input[type="text"]');
            
            if ($searchInput.length) {
                // Add search suggestions
                this.initSearchSuggestions($searchInput);
                
                // Add search history
                this.initSearchHistory($searchInput);
                
                // Focus management
                $searchInput.on('focus', function() {
                    $(this).closest('.search-form').addClass('focused');
                });
                
                $searchInput.on('blur', function() {
                    $(this).closest('.search-form').removeClass('focused');
                });
            }
        },

        /**
         * Initialize search suggestions
         *
         * @since 1.0.0
         * @param {jQuery} $input The search input element.
         */
        initSearchSuggestions($input) {
            // Placeholder for search suggestions functionality
            console.log('Search suggestions initialized');
        },

        /**
         * Initialize search history
         *
         * @since 1.0.0
         * @param {jQuery} $input The search input element.
         */
        initSearchHistory($input) {
            // Placeholder for search history functionality
            console.log('Search history initialized');
        },

        /**
         * Initialize map functionality
         *
         * Sets up interactive features for the home layout map.
         *
         * @since 1.0.0
         */
        initMap() {
            const $map = $('.wherebox-map');
            
            if ($map.length) {
                // Initialize map filters
                this.initMapFilters($map);
                
                // Initialize item highlighting
                this.initItemHighlighting($map);
                
                // Initialize tooltips
                this.initTooltips($map);
            }
        },

        /**
         * Initialize map filters
         *
         * @since 1.0.0
         * @param {jQuery} $map The map element.
         */
        initMapFilters($map) {
            // Placeholder for map filters functionality
            console.log('Map filters initialized');
        },

        /**
         * Initialize item highlighting
         *
         * @since 1.0.0
         * @param {jQuery} $map The map element.
         */
        initItemHighlighting($map) {
            // Placeholder for item highlighting functionality
            console.log('Item highlighting initialized');
        },

        /**
         * Initialize tooltips
         *
         * @since 1.0.0
         * @param {jQuery} $map The map element.
         */
        initTooltips($map) {
            // Placeholder for tooltips functionality
            console.log('Tooltips initialized');
        },

        /**
         * Initialize accessibility features
         *
         * Sets up keyboard navigation and screen reader support.
         *
         * @since 1.0.0
         */
        initAccessibility() {
            // Make item cards focusable
            $('.item-card').attr('tabindex', '0');
            
            // Add ARIA labels for item cards
            $('.item-card').each(function() {
                const itemName = $(this).find('.item-title').text();
                $(this).attr('aria-label', `Item: ${itemName}`);
                $(this).attr('role', 'button');
            });
            
            // Add ARIA labels for cards
            $('.container-card').each(function() {
                const containerName = $(this).find('.container-title').text();
                $(this).attr('aria-label', `Container: ${containerName}`);
            });
            
            $('.section-card').each(function() {
                const sectionName = $(this).find('.section-title').text();
                $(this).attr('aria-label', `Section: ${sectionName}`);
            });
            
            $('.furniture-card').each(function() {
                const furnitureName = $(this).find('.furniture-title').text();
                $(this).attr('aria-label', `Furniture: ${furnitureName}`);
            });
            
            // Announce state changes to screen readers
            $(document).on('DOMNodeInserted', '.collapsible-content.expanded', function() {
                $(this).attr('aria-live', 'polite');
            });
        },

        /**
         * Initialize collapsible sections
         *
         * Sets up collapsible functionality for furniture, sections, and containers.
         *
         * @since 1.0.0
         */
        initCollapsibleSections() {
            // Setup furniture collapsible sections
            $('.furniture-title').each(function() {
                const $title = $(this);
                const $card = $title.closest('.furniture-card');
                const $content = $card.find('.sections-grid').first();
                
                if ($content.length) {
                    $title.addClass('collapsible-trigger');
                    $content.addClass('collapsible-content');
                    
                    // Add ARIA attributes
                    const contentId = 'furniture-content-' + $card.data('furniture-id');
                    $content.attr('id', contentId);
                    $title.attr('aria-controls', contentId);
                    $title.attr('aria-expanded', 'false');
                    $title.attr('role', 'button');
                    $title.attr('tabindex', '0');
                }
            });

            // Setup section collapsible sections
            $('.section-title').each(function() {
                const $title = $(this);
                const $card = $title.closest('.section-card');
                const $content = $card.find('.containers-grid').first();
                
                if ($content.length) {
                    $title.addClass('collapsible-trigger');
                    $content.addClass('collapsible-content');
                    
                    // Add ARIA attributes
                    const contentId = 'section-content-' + $card.data('section-id');
                    $content.attr('id', contentId);
                    $title.attr('aria-controls', contentId);
                    $title.attr('aria-expanded', 'false');
                    $title.attr('role', 'button');
                    $title.attr('tabindex', '0');
                }
            });

            // Setup container collapsible sections
            $('.container-title').each(function() {
                const $title = $(this);
                const $card = $title.closest('.container-card');
                const $content = $card.find('.items-list').first();
                
                if ($content.length) {
                    $title.addClass('collapsible-trigger');
                    $content.addClass('collapsible-content');
                    
                    // Add ARIA attributes
                    const contentId = 'container-content-' + $card.data('container-id');
                    $content.attr('id', contentId);
                    $title.attr('aria-controls', contentId);
                    $title.attr('aria-expanded', 'false');
                    $title.attr('role', 'button');
                    $title.attr('tabindex', '0');
                }
            });
        },

        /**
         * Handle collapsible section click
         *
         * Toggles collapsible sections with smooth height transitions.
         *
         * @since 1.0.0
         * @param {Event} e The click event.
         */
        handleCollapsibleClick(e) {
            e.preventDefault();
            e.stopPropagation();
            
            const $trigger = $(this);
            WhereBoxPublic.toggleCollapsible($trigger);
        },

        /**
         * Handle collapsible section keypress
         *
         * Allows keyboard navigation for collapsible sections.
         *
         * @since 1.0.0
         * @param {Event} e The keypress event.
         */
        handleCollapsibleKeypress(e) {
            if (e.which === 13 || e.which === 32) { // Enter or Space
                e.preventDefault();
                e.stopPropagation();
                $(this).trigger('click');
            }
        },

        /**
         * Toggle collapsible section
         *
         * Handles the expand/collapse logic with smooth animations.
         *
         * @since 1.0.0
         * @param {jQuery} $trigger The trigger element.
         */
        toggleCollapsible($trigger) {
            const contentId = $trigger.attr('aria-controls');
            const $content = $('#' + contentId);
            const isExpanded = $trigger.attr('aria-expanded') === 'true';
            
            if (!$content.length) return;
            
            // Update ARIA attributes
            $trigger.attr('aria-expanded', !isExpanded);
            $trigger.toggleClass('expanded', !isExpanded);
            
            if (isExpanded) {
                // Collapse
                $content.removeClass('expanded');
                
                // Remove highlighting from parent card
                $trigger.closest('.furniture-card, .section-card, .container-card').removeClass('highlighted');
            } else {
                // Expand
                $content.addClass('expanded');
                
                // Add highlighting to parent card
                $trigger.closest('.furniture-card, .section-card, .container-card').addClass('highlighted');
                
                // Close other sections at the same level
                const $parentCard = $trigger.closest('.furniture-card, .section-card');
                if ($parentCard.length) {
                    $parentCard.siblings().find('.collapsible-trigger.expanded').each(function() {
                        WhereBoxPublic.toggleCollapsible($(this));
                    });
                }
            }
        },

        /**
         * Handle item card click
         *
         * Responds to clicks on individual item cards.
         *
         * @since 1.0.0
         * @param {Event} e The click event.
         */
        handleItemClick(e) {
            e.preventDefault();
            const $item = $(this);
            const itemId = $item.data('item-id');
            
            // Toggle selection
            $item.toggleClass('selected');
            
            // Show item details if available
            WhereBoxPublic.showItemDetails(itemId);
        },

        /**
         * Handle item card keypress
         *
         * Responds to keyboard interactions on item cards.
         *
         * @since 1.0.0
         * @param {Event} e The keypress event.
         */
        handleItemKeypress(e) {
            if (e.which === 13 || e.which === 32) { // Enter or Space
                e.preventDefault();
                $(this).trigger('click');
            }
        },

        /**
         * Handle room card click
         *
         * Responds to clicks on room cards (non-collapsible areas).
         *
         * @since 1.0.0
         * @param {Event} e The click event.
         */
        handleRoomClick(e) {
            const $room = $(this);
            const roomId = $room.data('room-id');
            
            // Highlight room
            $('.room-card').removeClass('highlighted');
            $room.addClass('highlighted');
            
            // Filter containers if needed
            WhereBoxPublic.filterContainersByRoom(roomId);
        },

        /**
         * Filter containers by room
         *
         * @since 1.0.0
         * @param {number} roomId The room ID.
         */
        filterContainersByRoom(roomId) {
            // Placeholder for filtering functionality
            console.log('Filter containers by room:', roomId);
        },

        /**
         * Handle filter change
         *
         * @since 1.0.0
         * @param {Event} e The change event.
         */
        handleFilterChange(e) {
            // Placeholder for filter change functionality
            console.log('Filter changed');
        },

        /**
         * Handle search form submit
         *
         * Processes search form submissions with validation.
         *
         * @since 1.0.0
         * @param {Event} e The submit event.
         */
        handleSearchSubmit(e) {
            const $form = $(this);
            const $input = $form.find('input[type="text"]');
            const searchTerm = $input.val().trim();
            
            if (searchTerm.length < 2) {
                e.preventDefault();
                WhereBoxPublic.showSearchError(whereboxPublic.strings.error);
                return false;
            }
            
            // Save to search history
            WhereBoxPublic.saveSearchHistory(searchTerm);
        },

        /**
         * Handle search input changes
         *
         * Provides live search suggestions as user types.
         *
         * @since 1.0.0
         * @param {Event} e The input event.
         */
        handleSearchInput(e) {
            const $input = $(this);
            const searchTerm = $input.val().trim();
            
            if (searchTerm.length >= 2) {
                WhereBoxPublic.fetchSearchSuggestions(searchTerm);
            } else {
                WhereBoxPublic.hideSuggestions();
            }
        },

        /**
         * Fetch search suggestions
         *
         * @since 1.0.0
         * @param {string} searchTerm The search term.
         */
        fetchSearchSuggestions(searchTerm) {
            // Placeholder for fetching suggestions
            console.log('Fetch suggestions for:', searchTerm);
        },

        /**
         * Hide suggestions
         *
         * @since 1.0.0
         */
        hideSuggestions() {
            // Placeholder for hiding suggestions
            console.log('Hide suggestions');
        },

        /**
         * Save search history
         *
         * @since 1.0.0
         * @param {string} searchTerm The search term.
         */
        saveSearchHistory(searchTerm) {
            // Placeholder for saving search history
            console.log('Save search term:', searchTerm);
        },

        /**
         * Show item details
         *
         * Displays detailed information about a selected item.
         *
         * @since 1.0.0
         * @param {number} itemId The item ID.
         */
        showItemDetails(itemId) {
            // Implementation for showing item details modal or inline
            console.log('Show details for item:', itemId);
        },

        /**
         * Show search error
         *
         * Displays error messages for search operations.
         *
         * @since 1.0.0
         * @param {string} message The error message.
         */
        showSearchError(message) {
            // Create or update error display
            let $error = $('.search-error');
            if (!$error.length) {
                $error = $('<div class="search-error" role="alert"></div>');
                $('.search-form').after($error);
            }
            
            $error.text(message).show();
            
            // Auto-hide after 3 seconds
            setTimeout(() => {
                $error.fadeOut();
            }, 3000);
        },

        /**
         * Trigger initial animations
         *
         * Adds animation classes to make elements visible with transitions.
         *
         * @since 1.0.0
         */
        triggerAnimations() {
            console.log('WhereBox: Triggering animations');
            
            // Add loaded class to main container
            $('.wherebox').addClass('loaded');
            
            // Trigger animations for main components with slight delays
            setTimeout(() => {
                $('.wherebox-map').addClass('animate-in');
                console.log('WhereBox: Map animation triggered');
            }, 100);
            
            setTimeout(() => {
                $('.wherebox-search').addClass('animate-in');
                console.log('WhereBox: Search animation triggered');
            }, 200);
            
            // Trigger animations for cards with staggered delays
            $('.item-card, .container-card, .section-card, .furniture-card, .room-card').each(function(index) {
                const $element = $(this);
                setTimeout(() => {
                    $element.addClass('animate-in');
                }, 300 + (index * 50));
            });
        },

        /**
         * Debounce function
         *
         * Limits the rate at which a function can fire.
         *
         * @since 1.0.0
         * @param {Function} func The function to debounce.
         * @param {number} wait The delay in milliseconds.
         * @return {Function} The debounced function.
         */
        debounce(func, wait) {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func.apply(this, args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        }
    };

    /**
     * Initialize when DOM is ready
     */
    jQuery(document).ready(function() {
        console.log('WhereBox: DOM ready, initializing...');
        WhereBoxPublic.init();
    });

    // Expose to global scope for external access
    window.WhereBoxPublic = WhereBoxPublic;
    
    console.log('WhereBox: Script loaded, object exposed to window');

})(jQuery);
